<template>
    <div v-if="!isExpired && !isBeforeStart" class="countdown-container" :class="{ 'clickable': this.targetUrl }" @click="navigateToUrl">

        <div class="countdown-header">
            <h2 v-html="headerText"></h2>
            <img src="@/assets/autodesk_techx_logo.png" alt="Logo" class="logo-icon" />
        </div>

        <div class="countdown-display">
            <div class="timer-segments">
                <div class="time-segment">
                    <span id="days" class="digit">{{ days }}</span>
                    <span class="label">DAYS</span>
                </div>
                <span class="colon digit">:</span>
                <div class="time-segment">
                    <span id="hours" class="digit">{{ hours }}</span>
                    <span class="label">HRS</span>
                </div>
                <span class="colon digit">:</span>
                <div class="time-segment">
                    <span id="minutes" class="digit">{{ minutes }}</span>
                    <span class="label">MINS</span>
                </div>
            </div>
        </div>

        <div class="countdown-footer">
            <span id="target-date" class="artifakt-font">
                {{ formattedFooterText }}
            </span>
        </div>

    </div>
</template>

<script>
export default {
    name: 'CountdownTimer',
    props: {
        // Target date passed as a string (e.g., "2025-04-30T00:00:00")
        targetDateString: {
            type: String,
            required: true,
        },
        startDateString: {
            type: String,
            required: true,
        },
        footerText: {
            type: String,
            default: null,
        },
        targetUrl: {
            type: String,
            default: null,
        },
        headerText: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            now: Date.now(), // Current timestamp, updated by interval
            interval: null,   // Holds the setInterval ID
        };
    },
    computed: {
        /**
         * Target date timestamp in milliseconds.
         */
        targetTimestamp() {
            return new Date(this.targetDateString).getTime();
        },
        /**
         * Remaining time in milliseconds.
         */
        distance() {
            return this.targetTimestamp - this.now;
        },
        /**
         * Flag indicating if the countdown has finished.
         */
        isExpired() {
            return this.distance <= 0;
        },
        /**
         * Flag indicating if the current time is before the start date.
         */
        isBeforeStart() {
            if (!this.startDateString) return false;
            const startTimestamp = new Date(this.startDateString).getTime();
            return this.now < startTimestamp;
        },
        /**
         * Remaining days, formatted with leading zero.
         */
        days() {
            if (this.isExpired) return '00';
            const days = Math.floor(this.distance / (1000 * 60 * 60 * 24));
            return String(days).padStart(2, '0');
        },
        /**
         * Remaining hours, formatted with leading zero.
         */
        hours() {
            if (this.isExpired) return '00';
            const hours = Math.floor((this.distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            return String(hours).padStart(2, '0');
        },
        /**
         * Remaining minutes, formatted with leading zero.
         */
        minutes() {
            if (this.isExpired) return '00';
            const minutes = Math.floor((this.distance % (1000 * 60 * 60)) / (1000 * 60));
            return String(minutes).padStart(2, '0');
        },
        formattedFooterText() {
            if (this.footerText) {
                return this.footerText;
            }

            // Default to the target date if no footer text is provided
            const options = { year: 'numeric', month: 'long', day: 'numeric' };
            const date = new Date(this.targetTimestamp);
            return isNaN(date) ? 'Invalid Date' : date.toLocaleDateString('en-US', options);
        },
        /**
         * Header text changes based on expiration status.
         */
        formattedHeaderText() {
            return '<span style="color: #E39A4D;">TechX</span> day 1 is in:'
        }
    },
    methods: {
        /**
         * Updates the current time and checks for expiration.
         */
        updateNow() {
            this.now = Date.now();
            // Stop the interval if the timer has expired
            if (this.isExpired && this.interval) {
                clearInterval(this.interval);
                this.interval = null;
            }
        },
        navigateToUrl() {
            if (!this.targetUrl) return;

            this.$hhMixpanel.trackEvent("Autodesk One - Countdown timer clicked");
            window.location.href = this.targetUrl;
        },
    },
    /**
     * When component is mounted: start the timer interval.
     */
    mounted() {
        this.updateNow(); // Initial update
        if (!this.isExpired) {
            this.interval = setInterval(this.updateNow, 1000);
        }
    },
    /**
     * Before component is destroyed: clear the interval.
     */
    beforeDestroy() {
        if (this.interval) {
            clearInterval(this.interval);
        }
    },
};
</script>

<style scoped>
/* Scoped styles apply only to this component */

.countdown-container {
    color: #ffffff;
    /* White text */
    max-width: 360px;
    /* Max width similar to image */
    margin-top: 0;
    margin-left: -20px;
    margin-right: -20px;
    /* Center the container */
    border-radius: 8px;
    /* Rounded corners */
    overflow: hidden;
    /* Clip content to rounded corners */
    font-family: 'artifaktElementRegular', Arial, sans-serif;
    /* Apply font globally */
}

.clickable {
    cursor: pointer;
    /* Show pointer cursor when clickable */
}

.countdown-header {
    display: flex;
    background-color: #000;
    /* Dark background */
    justify-content: space-between;
    align-items: center;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-right: 14px;
    padding-left: 14px;
}

.countdown-header h2 {
    font-family: 'artifaktLegendRegular' !important;
    font-weight: 600 !important;
    font-size: 24px !important;
    /* Slightly larger title */
    margin: 0;
    /* Remove default margin */
}

.countdown-display {
    padding-bottom: 15px;
    padding-right: 14px;
    padding-left: 14px;
    /* Padding */
    background-color: #000;
    /* Dark background */
    text-align: center;
    font-weight: 400;
  }
  
  .timer-segments {
    display: flex;
    justify-content: center;
    align-items: baseline;
    gap: 0.5rem;
    /* Space between segments */
    border: 0.5px solid #C64D22; /* Add white border */
    border-radius: 4px; /* Add border radius */
    background-color: #FFFFFF8F;
    padding-top: 8px;
    padding-bottom: 8px;
}

.time-segment {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 60px;
    /* Ensure segments have some width */
}

.digit {
    font-size: 37px !important;
    /* Large digits */
    line-height: 1;
    font-weight: 700;
    /* Medium weight */
    color: #000;
    /* White digits */
}

.colon {
    color: #000;
    /* Grey colon */
    padding: 0 0.25rem;
    /* Slight spacing around colon */
    /* Inherits .digit styles for size/weight */
}

.label {
    font-size: 11px;
    /* Smaller labels */
    color: #000;
    /* Grey labels */
    letter-spacing: 0.1em;
    /* Spacing */
    text-transform: uppercase;
}

.expired-message {
    font-size: 1.5rem;
    font-weight: bold;
    color: #FFFD54;
    /* Use footer yellow for emphasis */
    padding: 1rem 0;
    /* Add some padding */
}

.countdown-footer {
    background-color: #E39A4D;
    /* Specific yellow background */
    color: #000000;
    /* Black text */
    padding: 0.75rem 1rem;
    padding-top: 4px;
    padding-bottom: 2px;
    /* Padding */
    text-align: left;
    /* Left align date */
    font-size: 24px;
}

.artifakt-font {
    /* Apply specific styles for the date */
    font-weight: 700;
    /* Specific size */
    line-height: 1;
    /* Tight line height */
    letter-spacing: 0px;
    /* Normal letter spacing */
}

.logo-icon {
    width: 55px;
    height: 36px;
}

/* Ensure specific elements inherit the font */
.countdown-header h2,
.timer-segments .digit,
.timer-segments .label,
.expired-message {
    font-family: 'artifaktLegendRegular';
}

.countdown-footer {
    font-family: 'artifaktElementRegular';
    font-weight: 700;
}

.label {
  color: #000;
  font-weight: 400;
}

.digit {
  color: #000;
  font-weight: 700 !important;
}
</style>


// WEBPACK FOOTER //
// src/components/homebase/CountdownTimer.vue